import { fetchAuthSession } from 'aws-amplify/auth'
import { AxiosResponse } from 'axios'

import { handleException } from 'clients/exception-handling'
import type { ApiClient } from 'clients/yogi-api'
import {
  CreateProjectResponse,
  FormProduct,
  ProjectSourcesPayload,
  ProjectValidateSourcesPayload,
  ValidatePhaseEvent,
} from 'features/project-form/types'
import type { ProjectDataType, ProjectUsage } from 'features/projects/constants'

export type CreateProjectOptions = {
  usage?: ProjectUsage | null
  data_types?: ProjectDataType[]
  is_internal?: boolean | null
  account_uuid?: string | null
}

const PROJECT_CREATE_URL = '/project'
const SOURCE_CONNECT_URL = '/client/sources/connect'
const SOURCE_VALIDATE_CONNECT_STREAM_URL = '/source/connect/validate/stream'
const S3_FILE_UPLOAD = '/source/s3-file-upload'
const HUB_UUID_HEADER = 'X-Hub-UUID'
const PRODUCT_LIST_PROJECT_FORM = '/product/list'
const OLDEST_SOURCE_FROM_DATE_URL = '/project/sources/oldest-from-date'

export class ProjectFormService {
  constructor(private client: ApiClient) {}

  /**
   * Creates a new project
   * @param {string} title - Project name
   * @param {string} description - Project description
   * @param {CreateProjectOptions} options - Optional classification fields (usage, data_types, is_internal)
   * @returns {Promise<CreateProjectResponse>} UUID of created project
   */
  createProject(
    title: string,
    description: string,
    options: CreateProjectOptions = {},
  ): Promise<AxiosResponse<CreateProjectResponse>> {
    const body: {
      title: string
      description: string
      usage?: ProjectUsage
      data_types?: ProjectDataType[]
      is_internal?: boolean
      account_uuid?: string
    } = { title, description }
    if (options.usage != null) body.usage = options.usage
    if (options.data_types && options.data_types.length > 0)
      body.data_types = options.data_types
    if (options.is_internal != null) body.is_internal = options.is_internal
    if (options.account_uuid != null) body.account_uuid = options.account_uuid
    return this.client.post(PROJECT_CREATE_URL, body)
  }

  /**
   * Streams scope validation progress as SSE PhaseEvent frames. The terminal
   * `done` event carries the same 200/422 result the legacy endpoint would
   * have returned; an `error` event signals an internal 500. Callers should
   * surface phase + tasks_done/total + elapsed_ms in their loader UI.
   */
  async *validateProjectSourcesStream({
    sources,
    products,
    project_uuid,
    project_title,
  }: ProjectValidateSourcesPayload): AsyncGenerator<ValidatePhaseEvent> {
    const { tokens } = await fetchAuthSession()
    const token = tokens?.idToken?.toString() ?? ''
    const baseUrl = import.meta.env.VITE_YOGI_API_BASE_URL as string

    const response = await fetch(
      `${baseUrl}${SOURCE_VALIDATE_CONNECT_STREAM_URL}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          sources,
          products,
          project_uuid,
          project_title,
        }),
      },
    )

    if (!response.ok || !response.body) {
      throw new Error(`Validate stream failed: ${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            yield JSON.parse(line.slice(6)) as ValidatePhaseEvent
          } catch (cause) {
            handleException(cause, {
              operation: 'project.sources.progress.parse',
              disposition: 'expected',
              isExpectedError: (cause) => cause instanceof SyntaxError,
              reason: 'Malformed progress frame is ignored.',
            })

            // malformed line — skip
          }
        }
      }
    }
  }

  /**
   *
   * @param {string} proj_uuid - The project UUID
   * @param {Array<ProjectSourceFile | ProjectSourceURL>} sources - The list of project sources
   * @param {Array<Product>} products - The list of products
   * @param {string} stopWords - Reserved words for the project source
   * @param {boolean} parse_sentences
   * @param {boolean} bypass_nlp
   * @param {boolean} source_addition
   * @returns nothing
   */
  setProjectSources({
    sources,
    products,
    proj_uuid,
    stop_words,
    project_title,
    source_addition,
  }: ProjectSourcesPayload): Promise<AxiosResponse<void>> {
    return this.client.post(
      SOURCE_CONNECT_URL,
      {
        sources,
        products,
        stop_words,
        source_addition,
        sentiment_level: import.meta.env.VITE_ENV === 'prod' ? 'high' : 'low',
        bypass_validations: true,
        project_title,
      },
      { headers: { [HUB_UUID_HEADER]: proj_uuid }, timeout: 2_400_000 },
    )
  }

  /**
   *
   * @param {string} proj_uuid - Hub ID
   * @param {string} workingPath - The upload URL for the source
   * @param {File} file - The uploaded file
   * @param {string} bucket - Extra option
   * @returns nothing
   */
  uploadFile(file: File): Promise<AxiosResponse<{ path: string }>> {
    const formData = new FormData()
    formData.append('file', file)

    return this.client.post(S3_FILE_UPLOAD, formData)
  }

  async getOldestSourceFromDate(proj_uuid: string): Promise<string | null> {
    const { data } = await this.client.get<{ oldest_from_date: string | null }>(
      OLDEST_SOURCE_FROM_DATE_URL,
      {
        params: { proj_uuid },
      },
    )
    return data.oldest_from_date
  }

  async getProjectProducts(proj_uuid: string): Promise<FormProduct[]> {
    const { data } = await this.client.get<{ items: FormProduct[] }>(
      PRODUCT_LIST_PROJECT_FORM,
      {
        params: { proj_uuid },
      },
    )
    return data.items
  }
}
